import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/apiClient';
import Modal from '../ui/Modal';
import Breadcrumb from '../ui/Breadcrumb';
import Favicon from '../ui/Favicon';
import { moduleColor, orderModules } from '../ui/modulePalette';
import { IconAlertTriangle, IconEdit, IconGlobe, IconLayers, IconPlus, IconTrash } from '../ui/icons';

function siteTitle(s) {
  return (s.name || '').trim() || (s.domain || '').trim() || `Site #${s.id}`;
}

function clientLabel(c) {
  const company = (c.company_name || '').trim();
  const name = `${(c.first_name || '').trim()} ${(c.last_name || '').trim()}`.trim();
  return company || name || `Client #${c.id}`;
}

export default function AdminSitesPage() {
  const [state, setState] = useState({ status: 'loading', sites: [], clients: [], error: '' });
  const [modal, setModal] = useState(null);

  const adminOnlyKeys = useMemo(() => new Set(['clients', 'logs', 'admin_settings', 'sites']), []);
  const dbRequiredKeys = useMemo(() => new Set(['articles', 'projects', 'gallery', 'menus']), []);

  const refresh = () => {
    let cancelled = false;
    Promise.all([api.adminSites(), api.adminClients()])
      .then(([sitesRes, clientsRes]) => {
        if (cancelled) return;
        if (!sitesRes.ok) {
          setState({ status: 'error', sites: [], clients: [], error: sitesRes.error || 'Impossible de charger' });
          return;
        }
        if (!clientsRes.ok) {
          setState({ status: 'error', sites: [], clients: [], error: clientsRes.error || 'Impossible de charger les clients' });
          return;
        }
        setState({ status: 'ready', sites: sitesRes.sites || [], clients: clientsRes.clients || [], error: '' });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'error', sites: [], clients: [], error: 'Impossible de charger' });
      });
    return () => {
      cancelled = true;
    };
  };

  useEffect(() => refresh(), []);

  const sortedSites = useMemo(() => {
    if (state.status !== 'ready') return [];
    return [...state.sites].sort((a, b) => siteTitle(a).localeCompare(siteTitle(b), 'fr'));
  }, [state.sites, state.status]);

  const clientsOptions = useMemo(() => {
    if (state.status !== 'ready') return [];
    return [...state.clients].sort((a, b) => clientLabel(a).localeCompare(clientLabel(b), 'fr'));
  }, [state.clients, state.status]);

  async function saveSite(site) {
    const isEdit = Boolean(site.id);
    const res = isEdit ? await api.adminSiteUpdate(site) : await api.adminSiteCreate(site);
    if (!res.ok) return res;
    refresh();
    if (!isEdit) window.dispatchEvent(new CustomEvent('omniscope:sitesChanged'));
    return res;
  }

  async function deleteSite(id) {
    const res = await api.adminSiteDelete({ id });
    if (!res.ok) return res;
    refresh();
    window.dispatchEvent(new CustomEvent('omniscope:sitesChanged'));
    return res;
  }

  async function openSiteModules(site) {
    setModal({ type: 'site-modules', site, status: 'loading', modules: [], enabledKeys: ['dashboard'], error: '', saving: false });
    const res = await api.adminSiteModules(site.id);
    if (!res.ok) {
      setModal((m) => (m?.type === 'site-modules' ? { ...m, status: 'error', error: res.error || 'Erreur' } : m));
      return;
    }
    const list = (res.modules || []).filter((m) => !adminOnlyKeys.has(String(m.key)));
    const enabled = list.filter((m) => Boolean(m.is_enabled)).map((m) => String(m.key));
    const dbEnabled = enabled.includes('databases');
    const filteredEnabled = dbEnabled ? enabled : enabled.filter((k) => !dbRequiredKeys.has(k));
    if (!enabled.includes('dashboard')) enabled.unshift('dashboard');
    if (!filteredEnabled.includes('dashboard')) filteredEnabled.unshift('dashboard');
    setModal((m) => (m?.type === 'site-modules' ? { ...m, status: 'ready', modules: list, enabledKeys: filteredEnabled, error: '' } : m));
  }

  return (
    <div className="Page">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb items={[{ label: 'Sites' }]} />
        </div>
      </header>

      {state.status === 'error' ? (
        <div className="Card">
          <div className="Error" role="alert">
            {state.error}
          </div>
        </div>
      ) : null}

      <div className="Card">
        <div className="Row" style={{ marginBottom: 12 }}>
          <div className="TileTitle">Liste</div>
          <button
            type="button"
            className="Button"
            onClick={() =>
              setModal({
                type: 'edit-site',
                site: { name: '', url: '', assets_base_url: '', client_id: '', cms: '', hosting_provider: '', ga4_property_id: '', gsc_property: '', notes: '' },
                send_email: false,
                error: '',
                saving: false,
              })
            }
          >
            <span className="ButtonInline">
              <IconPlus /> Nouveau site
            </span>
          </button>
        </div>

        {state.status === 'loading' ? <div className="Muted">Chargement…</div> : null}
        {state.status === 'ready' && sortedSites.length === 0 ? <div className="Muted">Aucun site.</div> : null}

        {state.status === 'ready' ? (
          <div className="List" role="list">
            {sortedSites.map((s) => (
              <div className="ListItem" role="listitem" key={s.id}>
                <div>
                  <div className="UserName">
                    <Favicon url={s.url} faviconUrl={s.favicon_url} /> {siteTitle(s)}
                  </div>
                  <div className="Muted">
                    {s.url || '—'} {s.client ? ` · Client: ${clientLabel(s.client)}` : ''}
                  </div>
                </div>
                <div className="Badges AdminListItemActions">
                  <button type="button" className="Button Button--ghost" onClick={() => openSiteModules(s)}>
                    <span className="ButtonInline">
                      <IconLayers /> Modules
                    </span>
                  </button>
                  <button
                    type="button"
                    className="Button Button--ghost"
                    onClick={() =>
                      setModal({
                        type: 'edit-site',
                        site: {
                          id: s.id,
                          name: s.name || '',
                          url: s.url || '',
                          assets_base_url: s.assets_base_url || '',
                          client_id: s.client?.id ?? '',
                          cms: s.cms || '',
                          hosting_provider: s.hosting_provider || '',
                          ga4_property_id: s.ga4_property_id || '',
                          gsc_property: s.gsc_property || '',
                          notes: s.notes || '',
                        },
                        send_email: false,
                        error: '',
                        saving: false,
                      })
                    }
                  >
                    <span className="ButtonInline">
                      <IconEdit /> Modifier
                    </span>
                  </button>
                  <button type="button" className="Button Button--ghost" onClick={() => setModal({ type: 'delete-site', site: s })}>
                    <span className="ButtonInline">
                      <IconTrash /> Supprimer
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {modal?.type === 'edit-site' ? (
        <Modal
          title={modal.site.id ? 'Modifier un site' : 'Créer un site'}
          icon={<IconGlobe />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.saving}
                onClick={async () => {
                  setModal((m) => ({ ...m, saving: true, error: '' }));
                  const payload = {
                    ...modal.site,
                    client_id: modal.site.client_id === '' ? null : Number(modal.site.client_id),
                    send_email: Boolean(modal.send_email),
                  };
                  const res = await saveSite(payload);
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur' }));
                    return;
                  }
                  if (res.email && res.email.ok === false) {
                    setModal({ type: 'email-warning', error: res.email.error || 'Email non envoyé' });
                    return;
                  }
                  setModal(null);
                }}
              >
                {modal.saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </>
          }
        >
          <div className="Field">
            <label className="Label" htmlFor="site-name">
              Nom
            </label>
            <input
              id="site-name"
              className="Input"
              value={modal.site.name}
              onChange={(e) => setModal((m) => ({ ...m, site: { ...m.site, name: e.target.value } }))}
            />
          </div>
          <div className="Field">
            <label className="Label" htmlFor="site-url">
              URL
            </label>
            <input
              id="site-url"
              className="Input"
              value={modal.site.url}
              onChange={(e) => setModal((m) => ({ ...m, site: { ...m.site, url: e.target.value } }))}
            />
          </div>
          <div className="Field">
            <label className="Label" htmlFor="site-assets-base">
              Source assets (base URL)
            </label>
            <input
              id="site-assets-base"
              className="Input"
              value={modal.site.assets_base_url || ''}
              onChange={(e) => setModal((m) => ({ ...m, site: { ...m.site, assets_base_url: e.target.value } }))}
              placeholder="https://cdn.monsite.fr/assets"
            />
          </div>
          <div className="Field">
            <label className="Label" htmlFor="site-client">
              Client (principal)
            </label>
            <select
              id="site-client"
              className="Select"
              value={modal.site.client_id}
              onChange={(e) =>
                setModal((m) => ({
                  ...m,
                  site: { ...m.site, client_id: e.target.value },
                  send_email: false,
                }))
              }
            >
              <option value="">— Aucun —</option>
              {clientsOptions.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {clientLabel(c)}
                </option>
              ))}
            </select>
            {String(modal.site.client_id || '') ? (
              <label className="ButtonInline" style={{ gap: 10, marginTop: 10 }}>
                <input
                  type="checkbox"
                  checked={Boolean(modal.send_email)}
                  onChange={(e) => setModal((m) => ({ ...m, send_email: Boolean(e.target.checked) }))}
                  disabled={modal.saving}
                />
                <span>Envoyer un email au contact (optionnel)</span>
              </label>
            ) : null}
          </div>
          <div className="FormGrid">
            <div className="Field">
              <label className="Label" htmlFor="site-cms">
                CMS
              </label>
              <input
                id="site-cms"
                className="Input"
                value={modal.site.cms || ''}
                onChange={(e) => setModal((m) => ({ ...m, site: { ...m.site, cms: e.target.value } }))}
              />
            </div>
            <div className="Field">
              <label className="Label" htmlFor="site-hosting">
                Hébergeur
              </label>
              <input
                id="site-hosting"
                className="Input"
                value={modal.site.hosting_provider || ''}
                onChange={(e) => setModal((m) => ({ ...m, site: { ...m.site, hosting_provider: e.target.value } }))}
              />
            </div>
            <div className="Field">
              <label className="Label" htmlFor="site-ga4">
                GA4 Property ID
              </label>
              <input
                id="site-ga4"
                className="Input"
                value={modal.site.ga4_property_id || ''}
                onChange={(e) => setModal((m) => ({ ...m, site: { ...m.site, ga4_property_id: e.target.value } }))}
              />
            </div>
            <div className="Field">
              <label className="Label" htmlFor="site-gsc">
                GSC Property
              </label>
              <input
                id="site-gsc"
                className="Input"
                value={modal.site.gsc_property || ''}
                onChange={(e) => setModal((m) => ({ ...m, site: { ...m.site, gsc_property: e.target.value } }))}
              />
            </div>
          </div>
          <div className="Field">
            <label className="Label" htmlFor="site-notes">
              Notes
            </label>
            <textarea
              id="site-notes"
              className="Input"
              rows={4}
              value={modal.site.notes || ''}
              onChange={(e) => setModal((m) => ({ ...m, site: { ...m.site, notes: e.target.value } }))}
            />
          </div>
          {modal.error ? (
            <div className="Error" role="alert">
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'site-modules' ? (
        <Modal
          title="Modules du site"
          icon={<IconLayers />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" disabled={modal.saving} onClick={() => setModal(null)}>
                Fermer
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.saving || modal.status !== 'ready'}
                onClick={async () => {
                  setModal((m) => ({ ...m, saving: true, error: '' }));
                  const enabled = Array.from(new Set([...(modal.enabledKeys || []), 'dashboard']));
                  const res = await api.adminSiteModulesUpdate({ site_id: modal.site.id, enabled_keys: enabled });
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur' }));
                    return;
                  }
                  window.dispatchEvent(new CustomEvent('omniscope:modulesChanged', { detail: { siteId: modal.site.id } }));
                  setModal(null);
                }}
              >
                {modal.saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </>
          }
        >
          <div className="Row" style={{ marginBottom: 8 }}>
            <div className="UserName">
              <Favicon url={modal.site.url} faviconUrl={modal.site.favicon_url} /> {siteTitle(modal.site)}
            </div>
          </div>
          <div className="Muted" style={{ marginBottom: 10 }}>
            Active/désactive les modules visibles sur la sidebar (le Dashboard reste toujours actif).
          </div>

          {modal.status === 'loading' ? <div className="Muted">Chargement…</div> : null}
          {modal.status === 'error' ? (
            <div className="Error" role="alert">
              {modal.error || 'Erreur'}
            </div>
          ) : null}

          {modal.status === 'ready' ? (
            <div className="CheckGrid" role="group" aria-label="Modules du site">
              {orderModules(modal.modules || []).map((m) => {
                const checked = (modal.enabledKeys || []).includes(m.key) || m.key === 'dashboard';
                const databasesEnabled = (modal.enabledKeys || []).includes('databases');
                const requiresDatabases = Boolean(m.requires_databases) || dbRequiredKeys.has(String(m.key));
                const disabled = modal.saving || m.key === 'dashboard' || (requiresDatabases && !databasesEnabled);
                return (
                  <label key={m.key} className="Check" style={{ '--accent': moduleColor(m.key, m.color) }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={(e) => {
                        setModal((prev) => {
                          const next = new Set(prev.enabledKeys || []);
                          const key = String(m.key || '');
                          if (e.target.checked) next.add(key);
                          else next.delete(key);
                          if (key === 'databases' && !e.target.checked) {
                            (prev.modules || []).forEach((mod) => {
                              const mk = String(mod?.key || '');
                              const reqDb = Boolean(mod?.requires_databases) || dbRequiredKeys.has(mk);
                              if (reqDb) next.delete(mk);
                            });
                          }
                          next.add('dashboard');
                          return { ...prev, enabledKeys: Array.from(next) };
                        });
                      }}
                    />
                    <span className="CheckMain">
                      <span className="ColorDot" aria-hidden="true" />
                      <span style={{ minWidth: 0 }}>
                        {m.label} {m.coming_soon ? <span className="Badge">à venir</span> : null}
                        {requiresDatabases ? <span className="Badge">BDD</span> : null}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          ) : null}

          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'delete-site' ? (
        <Modal
          title="Suppression"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                onClick={async () => {
                  await deleteSite(modal.site.id);
                  setModal(null);
                }}
              >
                Supprimer
              </button>
            </>
          }
        >
          <div className="Muted">
            Supprimer <strong>{siteTitle(modal.site)}</strong> ?
          </div>
        </Modal>
      ) : null}

      {modal?.type === 'email-warning' ? (
        <Modal
          title="Email non envoyé"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
              Fermer
            </button>
          }
        >
          <div className="Error" role="alert">
            {modal.error || 'Email non envoyé.'}
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
